import { cx } from "class-variance-authority";
import React, { useEffect } from "react";
import { RiArrowUpDownLine } from "react-icons/ri";
import {
  components,
  ControlProps,
  InputProps,
  OptionProps,
} from "react-select";
import { FloSelectOption, useFloSelectAriaActiveDescendantContext } from ".";
import { DescribedBy, For, LabelProp } from "../input-general/label-prop";

const VALIDATION_MESSAGE_CLASSES = "text-sm text-ui-red italic";
export interface FormItemDecorationProps {
  label: Exclude<LabelProp, DescribedBy | For>;
  subText?: string;
}

interface RefWrapperProps {
  children: React.ReactNode;
  className: string;
}
export const RefDivWrapper = React.forwardRef<HTMLDivElement, RefWrapperProps>(
  (props, ref) => {
    if (ref)
      return (
        <div ref={ref} className={props.className}>
          {props.children}
        </div>
      );

    return <>{props.children}</>;
  },
);

const findOption = (
  inputValue: string,
  option: {
    label?: string;
  },
) => {
  if (!option.label) return false;
  return option?.label
    .toLocaleUpperCase()
    .includes((inputValue ?? "").toLocaleUpperCase());
};

const onKeyDown = (
  e: React.KeyboardEvent<HTMLInputElement>,
  props: InputProps<FloSelectOption>,
) => {
  // React-Select does not expose an easy way to use the keydown event
  // to prevent the enter key from firing the onChange event when nothing is selected
  // this causes the form to submit when the user hits enter in the input and nothing is selected
  // so we manually search for the option and prevent the event if it is not found
  if (e.key === "Enter") {
    const hasOption = props.options.some((o) =>
      findOption(props?.value?.toString() ?? "", o),
    );
    if (!hasOption) {
      e.preventDefault();
      e.stopPropagation();
    }
  }
};

export const InputWithEnterKeyFix = (props: InputProps<FloSelectOption>) => {
  const [ariaActiveDescendant] = useFloSelectAriaActiveDescendantContext();
  // Not able to put aria-activedescendant on component directly for some reason...
  const enhancedProps: InputProps<FloSelectOption> = {
    ...props,
    "aria-activedescendant": ariaActiveDescendant,
  };
  return (
    <components.Input
      {...enhancedProps}
      onKeyDown={(e) => onKeyDown(e, props)}
    />
  );
};

export const InputWithRequiredAndEnterKeyFix = (
  props: InputProps<FloSelectOption>,
) => {
  const [ariaActiveDescendant] = useFloSelectAriaActiveDescendantContext();
  const enhancedProps: InputProps<FloSelectOption> = {
    ...props,
    "aria-activedescendant": ariaActiveDescendant,
  };
  return (
    <components.Input
      {...enhancedProps}
      aria-required={true}
      onKeyDown={(e) => onKeyDown(e, props)}
    />
  );
};

export const SortControl = (props: ControlProps<FloSelectOption>) => {
  return (
    <components.Control {...props}>
      <RiArrowUpDownLine
        size={16}
        className={cx("ml-3.5 flex items-center justify-center")}
      />
      {props.children}
    </components.Control>
  );
};

export const AccessibleOption = (props: OptionProps<FloSelectOption>) => {
  const [_, setAriaActiveDescendant] =
    useFloSelectAriaActiveDescendantContext();
  const { isFocused, innerProps } = props;
  const { id } = innerProps;
  // https://reactjs.org/blog/2020/02/26/react-v16.13.0.html#warnings-for-some-updates-during-render
  useEffect(() => {
    if (isFocused) setAriaActiveDescendant(id ?? "");
  }, [isFocused, id, setAriaActiveDescendant]);
  return <components.Option {...props}>{props.children}</components.Option>;
};
